<template>
    <div class="location-request" v-if="visible">
        <div class="content">
            <img :src="LocationIcon" alt="location" class="icon" />

            <h2 class="title">
                {{ titleText }}
            </h2>

            <p class="desc">
                {{ descriptionText }}
            </p>

            <button class="allow-btn" :disabled="loading" @click="handleAllow">
                {{ loading ? "Определяем..." : buttonText }}
            </button>

            <p class="how-used" @click="toggleHelp">
                Как используется моё местоположение?
            </p>

            <div v-if="showHelp" class="help">
                Мы используем только примерное местоположение, чтобы показывать
                анкеты рядом. Точные координаты не сохраняются.
                Это является обязательным действием для корректной работы подгрузки анкет.
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import LocationIcon from "@/assets/images/subscription/Plus/2plus.svg?url";
import {
    requestAndSendCoordinates,
    getGeoPermissionState,
    isGeoAllowed
} from "@/api/coordinates";
import { showAppAlert } from "@/utils/appAlert";

const emit = defineEmits<{
    (e: "success"): void;
    (e: "denied"): void;
}>();

const visible = ref(true);
const loading = ref(false);
const permissionState = ref<"granted" | "prompt" | "denied" | "unknown">("unknown");
const showHelp = ref(false);

const titleText = computed(() => {
    if (permissionState.value === "granted") return "Геолокация уже определена";

    if (permissionState.value === "denied") return "Разреши доступ к геолокации";
    if (permissionState.value === "prompt") return "Определи своё местоположение";
    return "Где ты сейчас?";
});

const descriptionText = computed(() => {
    if (isGeoAllowed())
        return "Твоё местоположение уже определено — мы показываем людей рядом.";

    if (permissionState.value === "denied") {
        return "Без разрешения на геолокацию приложение не сможет показывать людей рядом. Включи доступ в настройках браузера.";
    }
    if (permissionState.value === "prompt") {
        return "Разреши определить твоё местоположение, чтобы мы могли показывать подходящих людей поблизости.";
    }
    return "Мы используем только примерные координаты, чтобы подбирать пары рядом с тобой.";
});

const buttonText = computed(() => {
    if (isGeoAllowed()) return "Продолжить";

    if (permissionState.value === "denied") return "Открыть настройки";
    if (permissionState.value === "prompt") return "Разрешить";
    return "Определить";
});

onMounted(async () => {
    permissionState.value = await getGeoPermissionState();
    console.log("[geo-modal] permission:", permissionState.value);

    if (permissionState.value === "granted") {
        loading.value = true;
        try {
            await requestAndSendCoordinates(false);
        } finally {
            loading.value = false;
            visible.value = false;
            emit("success");
        }
    }
});

function toggleHelp() {
    showHelp.value = !showHelp.value;
}

async function handleAllow() {
    if (loading.value) return;
    loading.value = true;

    try {
        const state = await getGeoPermissionState();

        if (state === "denied") {
            showAppAlert("Ты ранее запретил доступ к геолокации. Включи его в настройках браузера.", { title: "Доступ к геолокации" });
            loading.value = false;
            return;
        }

        await requestAndSendCoordinates(true);

        const newState = await getGeoPermissionState();

        if (newState === "granted" || isGeoAllowed()) {
            visible.value = false;
            emit("success");
            loading.value = false;
            return;
        }

        if (newState === "denied") {
            showAppAlert("Чтобы продолжать пользоваться TinderOne, включи доступ к геолокации в настройках браузера.", { title: "Доступ к геолокации" });
            loading.value = false;
            return;
        }

        showAppAlert("Не удалось определить местоположение. Попробуй снова.", { tone: "error" });
        loading.value = false;
        return;

    } catch (err) {
        console.warn("[geo] ошибка:", err);
        loading.value = false;

        const state = await getGeoPermissionState();
        if (state === "denied") {
            showAppAlert("Ты отклонил доступ. Включи геолокацию в настройках браузера.", { title: "Доступ к геолокации" });
        } else {
            showAppAlert("Не удалось определить местоположение. Попробуй ещё раз.", { tone: "error" });
        }
        return;
    }
}
</script>

<style scoped>
.location-request {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    padding: 24px;
    text-align: center;
}

.content {
    max-width: 360px;
}

.icon {
    width: 110px;
    height: 110px;
    margin: 0 auto 24px;
}

.title {
    font-size: 22px;
    margin-bottom: 12px;
    font-weight: 600;
}

.desc {
    font-size: 15px;
    color: #ddd;
    line-height: 1.4;
}

.allow-btn {
    width: 100%;
    margin-top: 28px;
    padding: 14px;
    border: none;
    border-radius: 30px;
    color: #fff;
    background: linear-gradient(90deg, #ff4458, #ff7a61);
    font-weight: 600;
    font-size: 16px;
    cursor: pointer;
}

.allow-btn[disabled] {
    opacity: 0.6;
    cursor: default;
}

.how-used {
    margin-top: 18px;
    color: #ccc;
    font-size: 14px;
    text-decoration: underline;
    cursor: pointer;
}

.help {
    margin-top: 10px;
    font-size: 13px;
    color: #aaa;
    line-height: 1.4;
}
</style>
